import { PlayerAdapterAutoplay } from 'library/ts/player/adapter/enums/PlayerAdapterAutoplay';
import { PlayerAdapterPerformanceMode } from 'library/ts/player/adapter/enums/PlayerAdapterPerformanceMode';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { EmbedDevice } from 'tag/ts/tag/model/type/embed/EmbedDevice';
import { ISTVPlatformConfig } from 'tag/ts/tag/player/stv/interfaces/ISTVPlatformConfig';
import { ISTVPlatformConfigsInfo } from 'tag/ts/tag/player/stv/interfaces/ISTVPlatformConfigsInfo';

/**
 * @copyright © Paramount Streaming
 * @description Base class for Smart TV platform-specific configuration and optimization
 *
 * **Smart TV Platform Configuration Framework:**
 * Provides the foundation for platform-specific video player optimizations across
 * the Smart TV ecosystem including Samsung Tizen, LG webOS, Xbox One, PlayStation,
 * and various cable/set-top box platforms.
 *
 * **Configuration Strategy:**
 * - **Default Settings**: Establishes baseline TV-optimized player and resource configurations
 * - **Platform Inheritance**: Subclasses override with platform-specific optimizations
 * - **Performance Tuning**: TV performance mode, advanced codecs, spatial audio preferences
 * - **Platform Detection**: Device-specific settings based on EmbedDevice classification
 *
 * **Key Configuration Areas:**
 * - **Player Options**: Autoplay policies, performance mode, text track rendering
 * - **Resource Config**: Playback settings, ABR configuration, codec preferences
 * - **Device Properties**: Platform identification and capability detection
 *
 * **Subclass Pattern:**
 * Each platform extends this base class to apply platform-specific optimizations:
 * ```typescript
 * export class SamsungTizen extends AbstractSTVPlatformConfig {
 *   constructor(params) {
 *     super(params);
 *     this.configs = this.utils.DeepMerge.deepmerge(this.configs, platformSpecificConfig);
 *   }
 * }
 * ```
 *
 * **Business Impact:**
 * Enables optimal video playback performance across diverse Smart TV hardware
 * while maintaining consistent Smart Tag functionality and user experience.
 */
export class AbstractSTVPlatformConfig implements ISTVPlatformConfig {

	// declare configs public prop
	configs: ISTVPlatformConfigsInfo;

	constructor( params: IParamsVO ) {
		// set default configs value
		this.configs = {
			playerOptions: {
				autoplay: PlayerAdapterAutoplay.ATTEMPT_UNMUTED,
				performanceMode: PlayerAdapterPerformanceMode.TV,
				renderTextTrackNatively: false
			},
			resourceConfig: {
				playback: {
					enableAdvancedCodecs: true,
					preferSpatialAudio: true
				}
			},
			properties: {
				device: EmbedDevice.UNKNOWN
			}
		};

		// removing capQualityToScreenSize for VOD
		if ( !params?.CONTENT?.isLive && this.configs?.resourceConfig?.playback?.abr?.capQualityToScreenSize ) {
			this.configs.resourceConfig.playback.abr.capQualityToScreenSize = false;
		}
	}

	/**
	 * @description Returns reference to SmartTag namespaceinstance of utilities
	 */
	get utils() {
		return window?.SmartTag?.utilities;
	}
}